

















































































































import Vue, { PropType } from "vue";
import draggable from "vuedraggable";
import { default as StrichScanner } from "@/components/SerialnumbersManager/StrichScanner.vue";
import SerialnumberEditor from "@/components/SerialnumbersManager/SerialnumberEditor.vue";
import { Nullable, SerialNumber, SerialNumberSource } from "@/types";
import normalizeSerialNumber, { INVERTER_SERIAL_LENGTH } from "@/helpers/serial-number";

import SerialnumberItem from "./SerialnumberItem.vue";

export default Vue.extend({
  components: {
    StrichScanner,
    SerialnumberEditor,
    SerialnumberItem,
    draggable
  },
  props: {
    required: Boolean,
    disabled: Boolean,
    numeric: Boolean,
    open: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: "Serienummers"
    },
    value: {
      type: Array as PropType<SerialNumber[]>,
      default: () => []
    },
    amountRequired: {
      type: Number,
      default: 0
    },
    patterns: { type: Array as PropType<string[]>, default: () => ["(.*?)"] },
    // How much of a scanned barcode is the serial; 0 keeps all of it. See serialFromScan().
    scanLength: { type: Number, default: INVERTER_SERIAL_LENGTH }
  },

  data() {
    return {
      isOpen: this.open ? 0 : -1,
      scanning: false,
      dragMode: false,
      empty: "",
      selectedIndex: -1
    };
  },

  computed: {
    selected(): Nullable<SerialNumber> {
      return this.selectedIndex >= 0 ? this.value[this.selectedIndex!] : null;
    },

    isDuplicate() {
      return (sn: string): boolean => !!this.value.find(val => sn === val.serialNumber);
    },

    isValidSerialnumber() {
      return (sn: SerialNumber): boolean =>
        this.patterns.some(
          (pattern: string) => sn.serialNumber && sn.serialNumber.match(new RegExp(pattern))
        );
    },

    amountValid(): number {
      return this.value.filter(val => this.isValidSerialnumber(val) && !this.hasDuplicate(val))
        .length;
    },

    hasDuplicate() {
      return (item: SerialNumber) => {
        if (!item.serialNumber) return false;

        const itemIndex = this.value.indexOf(item);

        const dupeIndex = this.value.findIndex(
          (val, index) => val.serialNumber === item.serialNumber && index !== itemIndex
        );

        return dupeIndex >= 0;
      };
    },

    duplicate(): SerialNumber | undefined {
      return this.value.find(item => this.hasDuplicate(item));
    },

    hasValidSerialnumbers(): boolean {
      return this.required ? this.amountRequired === this.amountValid : true;
    },

    isComplete(): boolean {
      return (
        this.value.length === this.amountRequired && this.value.every(val => !!val.serialNumber)
      );
    },

    icon() {
      return (source: SerialNumberSource) => {
        switch (source) {
          case "scan":
            return "mdi-barcode-scan";
          default:
            return "mdi-keyboard-outline";
        }
      };
    },

    dimensions() {
      return { width: window.innerWidth, height: (window.innerWidth / 16) * 9 };
    }
  },

  watch: {
    scanning(scanning: boolean, previous: boolean) {
      if (scanning === previous) return;

      if (scanning) {
        setTimeout(() => {
          const scanner = this.$refs.scanner as InstanceType<typeof StrichScanner>;
          scanner.start();
        }, 1);
      }
    },

    isComplete(val: boolean) {
      this.scanning = false;
    },

    amountRequired: {
      immediate: true,
      handler() {
        this.createPlaceholders();
      }
    }
  },

  methods: {
    handleEdit(item: SerialNumber) {
      if (this.disabled || this.dragMode) return;

      this.selectedIndex = this.value.indexOf(item);
    },

    createPlaceholders() {
      const serials = this.value ?? [];
      const numberOfSerials = serials.length ?? 0;
      if (this.amountRequired > numberOfSerials) {
        const extra: SerialNumber[] = new Array(this.amountRequired - numberOfSerials)
          .fill(null)
          .map(() => ({ serialNumber: "", source: "manual" } as SerialNumber));

        const placeholders = [...serials, ...extra];
        placeholders.forEach((placeholder, index) => {
          this.$set(this.value, index, placeholder);
        });
        this.$emit("input", placeholders);
      } else if (this.amountRequired < numberOfSerials) {
        const placeholders = serials.slice(0, this.amountRequired);
        placeholders.forEach((placeholder, index) => {
          this.$set(this.value, index, placeholder);
        });
        this.$emit("input", placeholders);
      }
    },

    onReorder(val: SerialNumber[]) {
      this.$emit("input", val);
    },

    toggleCamera() {
      this.scanning = !this.scanning;
    },

    toggleScan() {
      this.selectedIndex = -1;

      this.$nextTick(() => (this.selectedIndex = this.value.findIndex(item => !item.serialNumber)));
    },

    /* Item could be a single serialnumber or an array.
    In case of OCR it is possible to get multiple hits so in that case ignore the max amount. */
    async add(item: SerialNumber) {
      item = { ...item, serialNumber: normalizeSerialNumber(item.serialNumber) };

      if (this.isComplete || !item.serialNumber) {
        return false;
      }

      const possibleDuplicate = this.value.find(val => item.serialNumber === val.serialNumber);

      if (possibleDuplicate) {
        return false;
      }

      item = { ...item, created: Date.now() };

      let firstEmptyIndex = this.value.findIndex(item => !item.serialNumber);

      if (this.selected && !this.selected.serialNumber) {
        firstEmptyIndex = this.selectedIndex!;
        this.scanning = false;
      }

      if (firstEmptyIndex < 0) return false;

      this.value.splice(firstEmptyIndex, 1, item);

      this.$emit("input", this.value);

      return true;
    },

    onSave() {
      this.$emit("input", this.value);

      this.selectedIndex = this.value.findIndex(item => !item.serialNumber);
    },

    remove(item: SerialNumber) {
      const index = this.value.findIndex(i => i.serialNumber === item.serialNumber);

      if (index >= 0) {
        this.value.splice(index, 1, { serialNumber: "", source: "manual" });
        this.$emit("input", this.value);
      }
    }
  }
});
